import { useState, useEffect } from "react";
import { X, Calendar, Clock, Loader2, AlertCircle, Plus } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import Swal from "sweetalert2";
import { 
  obtenerHorariosZonaComun,
  HorarioZonaComun,
  obtenerTodosDiasUtilizacion,
  DiaUtilizacion
} from "../../../../api/zonaComunHorarioService";
import DetailDiaHabilModal from "./DetailDiaHabilModal";
import CreateDiaHabilModal from "./CreateDiaHabilModal";

interface DiasHabilitadosModalProps {
  isOpen: boolean;
  zonaComunCodigo: string;
  onClose: () => void;
}

export default function DiasHabilitadosModal({ 
  isOpen, 
  zonaComunCodigo,
  onClose 
}: DiasHabilitadosModalProps) {
  const [horarios, setHorarios] = useState<HorarioZonaComun[]>([]);
  const [diasUtilizacion, setDiasUtilizacion] = useState<DiaUtilizacion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedHorarioCodigo, setSelectedHorarioCodigo] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (isOpen && zonaComunCodigo) {
      loadDiasHabilitados();
      loadDiasUtilizacion();
    }
  }, [isOpen, zonaComunCodigo]);

  const loadDiasUtilizacion = async () => {
    try {
      const response = await obtenerTodosDiasUtilizacion();
      if (response.code === "1" && response.object_) {
        setDiasUtilizacion(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar días de utilización:", error);
    }
  };

  const loadDiasHabilitados = async () => {
    if (!zonaComunCodigo) return;

    try {
      setIsLoading(true);
      setError(null);

      const payload = {
        codigo: "",
        "zona comun codigo": zonaComunCodigo,
        "dia de utilizacion codigo": "",
      };

      const response = await obtenerHorariosZonaComun(payload);

      if (response.code === "1" && response.object_) {
        setHorarios(response.object_);
      } else {
        setHorarios([]);
        setError(response.message || "No se encontraron días habilitados");
      }
    } catch (error) {
      console.error("Error al cargar días habilitados:", error);
      setError("Error al cargar los días habilitados");
      setHorarios([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los días habilitados",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getNombreDia = (codigoDia: string): string => {
    const dia = diasUtilizacion.find(d => d.ddu_codigo === codigoDia);
    return dia ? dia.ddu_nombre : codigoDia;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Calendar size={28} />
            Días Habilitados
          </h2>
          {/* <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/50 font-semibold flex items-center gap-2"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Nuevo Día Hábil</span>
              <span className="sm:hidden">Nuevo</span>
            </Button>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-1 transition"
            >
              <X size={24} />
            </button>
          </div> */}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-military-600 mb-4" />
              <p className="text-gray-600">Cargando días habilitados...</p>
            </div>
          ) : error && horarios.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 text-center">{error}</p>
            </div>
          ) : horarios.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 text-center mb-4">
                No hay días habilitados configurados para esta zona común.
              </p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center gap-2"
              >
                <Plus size={20} />
                Agregar Primer Día Hábil
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {horarios.map((horario) => (
                  <div
                    key={horario.zhd_codigo}
                    className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer hover:border-military-400 transform hover:-translate-y-1 group"
                    onClick={() => {
                      setSelectedHorarioCodigo(horario.zhd_codigo);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-military-500 to-military-600 text-white rounded-xl p-3 shadow-lg group-hover:scale-110 transition-transform">
                          <Calendar size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-xl text-gray-900">
                            {getNombreDia(horario.zhd_dia_de_utilizacion)}
                          </h3>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mt-5">
                      <div className="flex items-center gap-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                        <div className="bg-green-500 text-white rounded-lg p-2">
                          <Clock size={18} />
                        </div>
                        <div className="flex-1">
                          <span className="text-xs font-semibold text-gray-600 uppercase">Apertura</span>
                          <p className="text-lg font-bold text-green-700">
                            {horario.zhd_hora_apertura}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 bg-gradient-to-r from-red-50 to-rose-50 rounded-lg p-3 border border-red-200">
                        <div className="bg-red-500 text-white rounded-lg p-2">
                          <Clock size={18} />
                        </div>
                        <div className="flex-1">
                          <span className="text-xs font-semibold text-gray-600 uppercase">Cierre</span>
                          <p className="text-lg font-bold text-red-700">
                            {horario.zhd_hora_cierre}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t pt-4 px-6 pb-6">
          <div className="flex justify-between items-center">
            <Button
              onClick={() => setShowCreateModal(true)}
              variant="outline"
              className="border-2 border-military-500 text-military-600 hover:bg-military-50 font-semibold flex items-center gap-2"
            >
              <Plus size={18} />
              Agregar Día Hábil
            </Button>
            <Button
              onClick={onClose}
              className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold"
            >
              Cerrar
            </Button>
          </div>
        </div>
      </div>

      {selectedHorarioCodigo && (
        <DetailDiaHabilModal
          isOpen={showDetailModal}
          horarioCodigo={selectedHorarioCodigo}
          zonaComunCodigo={zonaComunCodigo}
          onClose={(shouldReload) => {
            setShowDetailModal(false);
            setSelectedHorarioCodigo(null);
            if (shouldReload) {
              loadDiasHabilitados();
            }
          }}
        />
      )}

      <CreateDiaHabilModal
        isOpen={showCreateModal}
        zonaComunCodigo={zonaComunCodigo}
        onClose={(created) => {
          setShowCreateModal(false);
          if (created) {
            loadDiasHabilitados();
          }
        }}
      />
    </div>
  );
}

